import {
  faCoins,
  faEdit,
  faLanguage,
  faLink,
  faReceipt,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExpenseModel, GlobalSettingEnum } from 'api/model';
import EllipsisContextMenu from 'components/ContextMenu/EllipsisContextMenu';
import { ContextMenuOption } from 'components/ContextMenu';
import { emptyGuid } from 'constants/AppConstants';
import useTranslations from 'contexts/basicData/useTranslations';
import useUsers from 'contexts/basicData/useUsers';
import { FC, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DefaultEventHeader from '../EventsAndComments/eventComponents/DefaultEventHeader';
import ExpenseFileViewerModal from './ExpenseFileViewer';
import useMe from 'contexts/authentication/useMe';
import Roles from 'constants/Roles';
import { useNavigate } from 'react-router-dom';
import Routes from 'constants/Routes';
import useGlobalSettings from 'hooks/useGlobalSettings';
import useTranslateExpense from 'hooks/useTranslateExpense';

// Extended type until Orval is regenerated with translations field
type ExpenseModelWithTranslations = ExpenseModel & {
  translations?: Record<string, string>;
};

const parentHoverButton = 'parentHoverButton';
const EventSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};

  &:hover .${parentHoverButton}, ${`.${parentHoverButton}`}:focus-within {
    background: ${({ theme }) => theme.colors.background.button};
    color: ${({ theme }) => theme.colors.foreground.button};
  }
`;

const Message = styled.div<{ isSystemMessage?: boolean }>`
  padding: 10px 5px;
  white-space: pre-wrap;
  ${({ isSystemMessage, theme }) =>
    isSystemMessage && `background:${theme.colors.background.secondary}`}
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
`;

const ExpenseValue = styled.div`
  font-weight: 700;
`;

const ExpenseQuantity = styled.div`
  margin-left: 8px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
  font-weight: 500;
`;

const BottomRowRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin-left: auto;
`;

const LinkButton = styled.button`
  margin: 0;
  padding: 0;

  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.foreground.tint};
  font: inherit;
  font-weight: 500;
  text-align: start;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const TranslationToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.foreground.tint};
  cursor: pointer;
  font-size: 0.85em;
  padding: 4px 0;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

interface Props {
  expense: ExpenseModel;
  onEditExpense(expenseId: number): void;
  onDeleteExpense(expense: ExpenseModel): void;
}

const Expense: FC<Props> = ({ expense, onEditExpense, onDeleteExpense }) => {
  const navigate = useNavigate();
  const [showExpenseFile, setShowExpenseFile] = useState(false);
  const { paymentTypes, expenseTypes } = useTranslations();
  const users = useUsers();
  const me = useMe();
  const { parsedData: globalSettings } = useGlobalSettings();
  const hasTranslationAccess =
    globalSettings[GlobalSettingEnum.UsersWithAccessToTranslation]?.[
      me?.userID ?? -1
    ] === true;
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [showTranslated, setShowTranslated] = useState(false);

  const translateMutation = useTranslateExpense();

  const hasLineItemQuantity =
    expense.itemQuantity != null &&
    expense.itemQuantity > 0 &&
    !!expense.itemUnit;

  // Load cached translation from expense data on mount
  useEffect(() => {
    const expenseWithTranslations = expense as ExpenseModelWithTranslations;
    const cachedTranslation = expenseWithTranslations.translations?.Swedish;
    if (cachedTranslation) {
      setTranslatedText(cachedTranslation);
    }
  }, [expense]);

  const handleTranslateClick = useCallback(async () => {
    if (translatedText) {
      setShowTranslated(true);
      return;
    }

    try {
      const result = await translateMutation.mutateAsync({
        expenseId: expense.id,
        targetLanguage: 'Swedish',
      });
      setTranslatedText(result.translatedText);
      setShowTranslated(true);
    } catch {
      // translation failed silently — user can retry
    }
  }, [expense.id, translatedText, translateMutation]);

  const menuOptions = useMemo((): ContextMenuOption[] => {
    const opts: ContextMenuOption[] = [];

    if (hasTranslationAccess && !translatedText && expense.message) {
      opts.push({
        icon: <FontAwesomeIcon icon={faLanguage} />,
        label: translateMutation.isLoading ? 'Översätter...' : 'Översätt',
        onClick: handleTranslateClick,
      });
    }

    opts.push(
      {
        icon: <FontAwesomeIcon icon={faEdit} />,
        label: 'Redigera',
        onClick: () => onEditExpense(expense.id),
      },
      {
        icon: <FontAwesomeIcon icon={faTrash} />,
        label: 'Ta bort',
        onClick: () => onDeleteExpense(expense),
      }
    );

    return opts;
  }, [hasTranslationAccess, translatedText, expense, translateMutation.isLoading, handleTranslateClick, onEditExpense, onDeleteExpense]);

  return (
    <Suspense fallback="Laddar...">
      <EventSection key={expense.id}>
        <DefaultEventHeader
          created={expense.created}
          denied={expense.deniedBy !== null}
          headerText={
            <>
              <FontAwesomeIcon icon={faCoins} />{' '}
              {expenseTypes[expense.expenseType]} -{' '}
              {paymentTypes[expense.paymentType]}
            </>
          }
          modified={expense.modified}
          modifiedByID={expense.modifiedByID}
          createdByID={expense.createdByID}
          userName={
            expense.createdByID === -1
              ? 'System'
              : users[expense.createdByID]?.name
          }
          assignmentId={expense.assignmentID}
        />

        <Message>
          {hasTranslationAccess && showTranslated && translatedText
            ? translatedText
            : expense.message}
        </Message>
        {hasTranslationAccess && translatedText && (
          <TranslationToggle onClick={() => setShowTranslated(!showTranslated)}>
            <FontAwesomeIcon icon={faLanguage} />
            {showTranslated ? 'Visa original' : 'Visa översättning'}
          </TranslationToggle>
        )}
        {expense.systemMessage !== null && (
          <Message isSystemMessage>{expense.systemMessage}</Message>
        )}

        <BottomRow>
          <ExpenseValue>{expense.value}kr</ExpenseValue>
          {hasLineItemQuantity && (
            <ExpenseQuantity>
              {expense.itemQuantity!.toLocaleString('sv-SE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              {expense.itemUnit}
            </ExpenseQuantity>
          )}

          <BottomRowRight>
            {expense.externalReceiptLineItemID !== null && (
              <LinkButton
                onClick={() => {
                  navigate(
                    `${Routes.excelExports.receipts}?lineItemId=${expense.externalReceiptLineItemID}`
                  );
                }}
              >
                Kvitto <FontAwesomeIcon icon={faLink} />
              </LinkButton>
            )}
            {expense.fileGuid !== emptyGuid && (
              <LinkButton onClick={() => setShowExpenseFile(true)}>
                Kvitto <FontAwesomeIcon icon={faReceipt} />
              </LinkButton>
            )}
          </BottomRowRight>

          {me?.roles.includes(Roles.Admin) && (
            <EllipsisContextMenu
              className={parentHoverButton}
              options={menuOptions}
              position="left"
            />
          )}
        </BottomRow>

        {showExpenseFile && (
          <ExpenseFileViewerModal
            expense={expense}
            onClose={() => setShowExpenseFile(false)}
          />
        )}
      </EventSection>
    </Suspense>
  );
};

export default Expense;
